import React from "react";
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import {
  ChartBox, CHART_MARGIN, GRID, xAxisProps, yAxisProps,
  compactAxis, num, renderRefLines, SUCCESS, DANGER,
} from "./_kit";
import { CategoryTip } from "./recharts.styles";

const trackStatus = (actual, required) => {
  if (actual == null || required == null) return null;
  const onTrack = actual <= required;
  return { onTrack, pct: required > 0 ? Math.abs((actual - required) / required) * 100 : null };
};

function CategoryTooltip({ active, payload, label, cats, colorOf }) {
  if (!active || !payload?.length) return null;
  return (
    <CategoryTip>
      <div className="yr">{label}</div>
      {cats.map((c) => {
        const req = payload.find((p) => p.dataKey === `req_${c.categoryKey}`);
        const act = payload.find((p) => p.dataKey === `act_${c.categoryKey}`);
        const hasReq = req && req.value != null;
        const hasAct = act && act.value != null;
        if (!hasReq && !hasAct) return null;
        const s = hasReq && hasAct ? trackStatus(act.value, req.value) : null;
        return (
          <div key={c.categoryKey} style={{ color: colorOf(c.categoryKey), marginTop: 2 }}>
            {c.name}: {hasReq ? `req ${num(req.value)}` : ""}
            {hasReq && hasAct ? " · " : ""}
            {hasAct ? `actual ${num(act.value)}` : ""}
            {s?.pct != null && (
              <span style={{ fontWeight: 700, color: s.onTrack ? SUCCESS : DANGER }}>{` (${s.onTrack ? "✓" : "✗"} ${s.pct.toFixed(1)}%)`}</span>
            )}
          </div>
        );
      })}
    </CategoryTip>
  );
}

/**
 * NetZero Scope-3 chart: a required (solid) + actual (dashed) line per category,
 * with near-term / net-zero reference lines and an on-track tooltip.
 * @param {{categoryKey:string,name:string,pathway:{year:number,required?:number,actual?:number}[]}[]} props.cats
 * @param {number} [props.nearTermYear]
 * @param {number} [props.netZeroYear]
 * @param {(categoryKey:string)=>string} props.colorOf
 */
export function CategoryLinesChart({ cats, nearTermYear, netZeroYear, colorOf }) {
  const base = cats[0]?.pathway || [];
  const chartData = base.map((row) => {
    const point = { year: row.year };
    for (const c of cats) {
      const r = c.pathway.find((x) => x.year === row.year);
      point[`req_${c.categoryKey}`] = r?.required ?? null;
      point[`act_${c.categoryKey}`] = r?.actual ?? null;
    }
    return point;
  });

  const refLines = [
    nearTermYear && { x: nearTermYear, label: `Near-term ${nearTermYear}` },
    netZeroYear && { x: netZeroYear, label: `Net-zero ${netZeroYear}` },
  ].filter(Boolean);

  return (
    <ChartBox>
      <ResponsiveContainer>
        <ComposedChart data={chartData} margin={CHART_MARGIN}>
          <CartesianGrid stroke={GRID} vertical={false} />
          <XAxis dataKey="year" {...xAxisProps} />
          <YAxis {...yAxisProps} tickFormatter={compactAxis} />
          <Tooltip content={<CategoryTooltip cats={cats} colorOf={colorOf} />} />
          {renderRefLines(refLines)}
          {cats.map((c) => (
            <Line key={`req_${c.categoryKey}`} type="monotone" dataKey={`req_${c.categoryKey}`}
              stroke={colorOf(c.categoryKey)} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
          ))}
          {cats.map((c) => (
            <Line key={`act_${c.categoryKey}`} type="monotone" dataKey={`act_${c.categoryKey}`}
              stroke={colorOf(c.categoryKey)} strokeWidth={1.5} strokeDasharray="5 4"
              dot={{ r: 3, fill: colorOf(c.categoryKey), strokeWidth: 0 }} connectNulls={false} isAnimationActive={false} />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartBox>
  );
}
